import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { money } from "@/lib/api";
import Reveal from "./Reveal";

const Cell = ({ product, onSelect, className = "" }) => (
  <button
    data-testid={`bento-${product.id}`}
    onClick={() => onSelect(product)}
    className={`card-spotlight group relative overflow-hidden rounded-2xl border border-voltaic-line bg-voltaic-surface text-left ${className}`}
  >
    <img
      src={product.image}
      alt={product.name}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
    <div className="relative flex h-full flex-col justify-end p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-voltaic-cyan">{product.category}</p>
      <div className="mt-1 flex items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold leading-tight sm:text-xl">{product.name}</h3>
          <p className="mt-0.5 text-sm font-semibold text-voltaic-muted">{money(product.price)}</p>
        </div>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-voltaic-linebright opacity-0 transition-all duration-300 group-hover:opacity-100">
          <Check size={14} className="hidden" />
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 17 17 7M8 7h9v9" />
          </svg>
        </span>
      </div>
    </div>
  </button>
);

export default function BentoFeatured({ products, onSelect }) {
  const [copied, setCopied] = useState(false);
  const byId = (id) => products.find((p) => p.id === id);
  const hero = byId("blade-x16");
  const second = byId("cyberphone-ultra");
  const third = byId("chrono-apex");
  if (!hero || !second || !third) return null;

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText("VOLTAIC20");
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-12 md:auto-rows-[215px]">
      <Cell product={hero} onSelect={onSelect} className="min-h-[300px] md:col-span-7 md:row-span-2" />
      <Cell product={second} onSelect={onSelect} className="min-h-[220px] md:col-span-5" />
      <Cell product={third} onSelect={onSelect} className="min-h-[180px] md:col-span-3" />
      <div
        data-testid="bento-promo"
        className="relative flex min-h-[180px] flex-col justify-between overflow-hidden rounded-2xl border border-voltaic-cyan/40 bg-voltaic-cyan p-5 text-voltaic-bg md:col-span-2"
      >
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em]">Limited</p>
        <div>
          <p className="font-display text-3xl font-extrabold leading-none">20%</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em]">off at checkout</p>
        </div>
        <button
          data-testid="bento-copy-promo"
          onClick={copyCode}
          className="flex items-center gap-2 self-start rounded-full bg-voltaic-bg px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-cyan"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "VOLTAIC20"}
        </button>
      </div>
    </div>
  );
}
